import './UserDetailsModal.scss';
import { Modal } from "../../Smart/Modal/Modal";
import React, {useContext, useState, useMemo, useCallback } from "react";
import {getUserSignDocsByAdminAPI, removeUserSignDocAPI, markSignDocUserAsCompletedAPI, unmarkSignDocUserAsCompletedAPI, approveOrRejectUserUploadAPI} from "../../../Services/api";
import {getAllSignDocTypes, isSignDocComplete, SignDocTypes} from "../../SignDoc/SignDocLib";
import {I18nReactContext} from "../../../contexts/I18nContext";
import {CompanyReactContext} from "../../../contexts/CompanyContext";
import {GridReactContextProvider} from "../../../Utils/DataGridWrapper/GridContext";
import {DataGridWrapper} from "../../../Utils/DataGridWrapper/DataGridWrapper";
import {ProgressBar} from "../../ProgressBar/ProgressBar";
import {StatusAndCompleteCell} from "../StatusAndCompleteCell/StatusAndCompleteCell";
import {get, map, filter, isEmpty} from "lodash";
import {format} from "date-fns";
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {IconButton, Switch} from "@mui/material";
import {FilePreview, isPreviewableFile} from "../../Smart/FilePreview/FilePreview";
import {SignDocApprovalActions} from "../../SignDoc/SignDocApprovalActions/SignDocApprovalActions";
import {SignDocCategories, FeatureFlags, UserSignDocStatus} from "../../../Utils/Constants";
import {ApproveUploadModal} from "../../LearningManagement/PendingApprovalsPage/ApproveUploadModal/ApproveUploadModal";
import {RejectUploadModal} from "../../LearningManagement/PendingApprovalsPage/RejectUploadModal/RejectUploadModal";


/**
 * Tells whether a row is a learning path that can be expanded into its tasks.
 * A path with no tasks is not expandable.
 * @param {object} row - A grid row
 * @returns {boolean} True when the row is a learning path holding at least one task
 */
const isExpandableLearningPathRow = (row) => (
    get(row, 'type') === SignDocTypes.LEARNING_PATH && !isEmpty(get(row, 'learningPathChildren', []))
)

/**
 * Builds the columns for the user details table.
 * @param {object} params - Function parameters
 * @param {object} params.i18n - Internationalization context
 * @param {function} params.removeUserFromSignDoc - Removes the user from a SignDoc
 * @param {function} params.markCompleted - Marks a SignDoc as completed for the user
 * @param {function} params.unmarkCompleted - Undoes an admin completion
 * @param {function} params.handlePreview - Opens the file preview modal
 * @param {function} params.handleApproveAction - Approve handler for the actions column
 * @param {function} params.handleRejectAction - Reject handler for the actions column
 * @param {boolean} params.hasCertificationFF - Whether the certification feature flag is on
 * @param {function} params.toggleLearningPath - Expands or collapses a learning path row
 * @returns {Array} Array of column definitions
 */
const getUserDetailsColumns = ({i18n, removeUserFromSignDoc, markCompleted, unmarkCompleted, handlePreview, handleApproveAction, handleRejectAction, hasCertificationFF, toggleLearningPath}) => {
    const baseColumns = [
        {
            field: 'docName',
            headerName: i18n.translate("deletedUserModal.columns.docName"),
            minWidth: 250,
            align: 'right',
            sortable: false,
            renderCell: (params) => {
                const uploadedFile = get(params.row, 'uploadedFile');
                const showEyeIcon = params.row.type === SignDocTypes.UPLOAD_DOCUMENT &&
                                   uploadedFile &&
                                   uploadedFile.signedUrl &&
                                   isPreviewableFile(uploadedFile.mimeType);

                const isExpandableLearningPath = isExpandableLearningPathRow(params.row);
                // isExpanded is read from the row, not from a closure, because the columns are built
                // once and a closure would keep a stale value
                const isExpanded = get(params, 'row.isExpanded', false);
                const isLearningPathChild = get(params, 'row.isLearningPathChild', false);
                const learningPathOrder = get(params, 'row.learningPathOrder', null);

                return (
                    <div className={`UserDetailsModal__doc-name-cell ${isLearningPathChild ? 'UserDetailsModal__doc-name-cell--lp-child' : ''}`}>
                        {isExpandableLearningPath && (
                            <IconButton
                                onClick={() => toggleLearningPath(params.row.id)}
                                className="UserDetailsModal__lp-expand-button"
                                data-testid="learning-path-expand-toggle"
                                aria-label={i18n.translate(isExpanded ? 'userDetailsModal.learningPath.collapseTasks' : 'userDetailsModal.learningPath.expandTasks')}
                                title={i18n.translate(isExpanded ? 'userDetailsModal.learningPath.collapseTasks' : 'userDetailsModal.learningPath.expandTasks')}
                                size="small"
                            >
                                {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                        )}
                        {isLearningPathChild && learningPathOrder && (
                            <span className="UserDetailsModal__lp-child-order">{`${learningPathOrder}.`}</span>
                        )}
                        <span>{params.value}</span>
                        {get(params, 'row.status') === UserSignDocStatus.ARCHIVED && (
                            <span className="UserDetailsModal__archived-tag">
                                {i18n.translate('userDetailsModal.archivedTag')}
                            </span>
                        )}
                        {showEyeIcon && (
                            <IconButton
                                onClick={() => handlePreview(uploadedFile)}
                                className="viewDocumentButton"
                                aria-label="view document"
                                size="small"
                                style={{padding: '4px'}}
                            >
                                <VisibilityIcon fontSize="small" />
                            </IconButton>
                        )}
                    </div>
                );
            },
        },
        {
            field: 'type',
            headerName: i18n.translate("deletedUserModal.columns.type"),
            minWidth: 130,
            align: 'right',
            sortable: false,
            renderCell: (params) => {
                const typeText = i18n.translate(`signDocTypesNotForCompletion.${params.value}`);
                const doNotForceCompletion = get(params, 'row.doNotForceCompletion', false);
                const displayText = doNotForceCompletion ? `${typeText} - ${i18n.translate("deletedUserModal.signDocData.doNotForceCompletion")}` : typeText;
                return <div style={{width: '100%'}} title={displayText}>{displayText}</div>;
            },
        },
        {
            field: 'category',
            headerName: i18n.translate("deletedUserModal.columns.category"),
            minWidth: 150,
            align: 'right',
            sortable: false,
            renderCell: (params) => {
                const category = params.value;
                if (!category) return <div style={{width: '100%'}}>-</div>;

                let categoryText = '';
                switch(category) {
                    case SignDocCategories.FORCE_COMPLETION:
                        categoryText = i18n.translate('certifications.categoryForceCompletion');
                        break;
                    case SignDocCategories.CERTIFICATION:
                        categoryText = i18n.translate('certifications.categoryCertification');
                        break;
                    case SignDocCategories.KNOWLEDGE_CENTER:
                        categoryText = i18n.translate('certifications.categoryKnowledgeCenter');
                        break;
                    default:
                        categoryText = category;
                }
                return <div style={{width: '100%'}} title={categoryText}>{categoryText}</div>;
            },
        },
        {
            field: 'status',
            headerName: i18n.translate("deletedUserModal.columns.status"),
            minWidth: 250,
            align: 'right',
            sortable: false,
            renderCell: (params) => (
                <StatusAndCompleteCell
                    readOnly={get(params, 'row.status') === UserSignDocStatus.ARCHIVED}
                    signDoc={params.row}
                    i18n={i18n}
                    // A task is assigned through its learning path, so it cannot be removed on its own
                    removeUserFromSignDoc={get(params, 'row.isLearningPathChild', false) ? undefined : removeUserFromSignDoc}
                    markCompleted={markCompleted}
                    unmarkCompleted={unmarkCompleted}
                    isSignDocValid={true}
                />
            ),
        },
        {
            field: 'completedOnDate',
            headerName: i18n.translate("deletedUserModal.columns.completedOnDate"),
            minWidth: 150,
            align: 'right',
            sortable: false,
            renderCell: (params) => <div style={{width: '100%'}} title={params.value}>{params.value ? format(params.value, 'dd/MM/yyyy') : ''}</div>,
        },
    ];

    // Expiry date column - only shown if CERTIFICATION feature flag is enabled
    const expiryDateColumn = hasCertificationFF ? [{
        field: 'expiryDate',
        headerName: i18n.translate("deletedUserModal.columns.expiryDate"),
        minWidth: 150,
        align: 'right',
        sortable: false,
        renderCell: (params) => {
            const isCertification = get(params, 'row.category', '') === SignDocCategories.CERTIFICATION;
            const expiryDate = params.value;

            // Only show expiry date for certifications
            if (!isCertification) {
                return <div style={{width: '100%'}}>-</div>;
            }

            return <div style={{width: '100%'}} title={expiryDate ? format(new Date(expiryDate), 'dd/MM/yyyy') : ''}>
                {expiryDate ? format(new Date(expiryDate), 'dd/MM/yyyy') : '-'}
            </div>;
        },
    }] : [];

    // Actions column - always shown (for courses and certifications)
    const actionsColumn = [{
        field: 'actions',
        headerName: i18n.translate("deletedUserModal.columns.actions"),
        minWidth: 120,
        align: 'center',
        sortable: false,
        renderCell: (params) => {
            // Archived assignments are history - approve/reject would mutate a superseded row
            if (get(params, 'row.status') === UserSignDocStatus.ARCHIVED) {
                return null;
            }

            const signDocType = get(params, 'row.type', '');
            const isCurrentUserApprover = get(params, 'row.isCurrentUserApprover', false);

            return (
                <SignDocApprovalActions
                    row={params.row}
                    signDocType={signDocType}
                    isCurrentUserApprover={isCurrentUserApprover}
                    onApprove={handleApproveAction}
                    onReject={handleRejectAction}
                />
            );
        },
    }];

    return [...baseColumns, ...expiryDateColumn, ...actionsColumn];
}


/**
 * UserProgressSummary Component
 * Displays progress summary with counts and progress bar
 */
const UserProgressSummary = ({ signDocsData, i18n }) => {
    const progressStats = useMemo(() => {
        if (!signDocsData || signDocsData.length === 0) {
            return { completed: 0, inProgress: 0, notStarted: 0, total: 0, percentage: 0 };
        }
        
        // Filter out signDocs with doNotForceCompletion: true from statistics
        const filteredSignDocsData = filter(signDocsData, signDoc => {
            return !get(signDoc, 'signDoc.doNotForceCompletion', false);
        });
        
        let completed = 0;
        let inProgress = 0;
        let notStarted = 0;
        
        filteredSignDocsData.forEach(signDoc => {
            if (isSignDocComplete({ signDoc })) {
                completed++;
            } else if (signDoc.progressPercent > 0) {
                inProgress++;
            } else {
                notStarted++;
            }
        });
        
        const total = filteredSignDocsData.length;
        const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
        
        return { completed, inProgress, notStarted, total, percentage };
    }, [signDocsData]);
    
    
    return (
        <div className="UserDetailsModal__progress-summary">
            <h3 className="UserDetailsModal__progress-title">
                {i18n.translate('userDetailsModal.progressSummary.title')}
            </h3>
            
            <div className="UserDetailsModal__progress-stats">
              
            <div className="UserDetailsModal__stat-item UserDetailsModal__stat-item--notStarted">
                    <span className="UserDetailsModal__stat-number">{progressStats.notStarted}</span>
                    <span className="UserDetailsModal__stat-label">
                        {i18n.translate('userDetailsModal.progressSummary.notStarted')}
                    </span>
                </div>
                
                <div className="UserDetailsModal__stat-item UserDetailsModal__stat-item--inProgress">
                    <span className="UserDetailsModal__stat-number">{progressStats.inProgress}</span>
                    <span className="UserDetailsModal__stat-label">
                        {i18n.translate('userDetailsModal.progressSummary.inProgress')}
                    </span>
                </div>
                
              

                <div className="UserDetailsModal__stat-item UserDetailsModal__stat-item--completed">
                    <span className="UserDetailsModal__stat-number">{progressStats.completed}</span>
                    <span className="UserDetailsModal__stat-label">
                        {i18n.translate('userDetailsModal.progressSummary.completed')}
                    </span>
                </div>
            </div>
            
            <div className="UserDetailsModal__progress-bar-section">
                <div className="UserDetailsModal__progress-bar-wrapper">
                    <ProgressBar 
                        percentage={progressStats.percentage} 
                        size="large" 
                        statusText={`${progressStats.completed}/${progressStats.total} ${i18n.translate('userDetailsModal.progressSummary.tasksCompleted')}`}
                        className="UserDetailsModal__custom-progress"
                    />
                </div>
            </div>
        </div>
    );
};

const tableFilters = {
    showFilters: {
        search: false,
        organizations: false
    },
    filterValues: {},
    filterData: {}
}

/**
 * Converts a single UserSignDoc record from the API into a grid row.
 * Learning path tasks use this same converter, so a task row has the exact same shape as any other row
 * and every column renders it without a special case.
 *
 * @param {object} userSignDoc - UserSignDoc record from the API
 * @returns {object} Grid row
 */
const convertUserSignDocToRow = (userSignDoc) => {
    // Get uploaded file for UPLOAD_DOCUMENT type from UserSignDoc additionalData
    // Backend stores files at userSignDoc.additionalData.files (not signDoc.additionalData)
    const uploadedFiles = get(userSignDoc, 'additionalData.files', []) ||
                         get(userSignDoc, 'dataValues.additionalData.files', []);
    const uploadedFile = uploadedFiles.length > 0 ? uploadedFiles[0] : null;

    return {
        docName: get(userSignDoc, 'signDoc.docName', ''),
        progressPercent: get(userSignDoc, 'progressPercent', 0),
        type: get(userSignDoc, 'signDoc.type', ''),
        category: get(userSignDoc, 'signDoc.category', ''),
        doNotForceCompletion: get(userSignDoc, 'signDoc.doNotForceCompletion', false),
        id: get(userSignDoc, 'signDoc.id', ''),
        userSignDocId: get(userSignDoc, 'id', ''),
        completedOnDate: get(userSignDoc, 'completedOnDate', ''),
        examScore: get(userSignDoc, 'examScore', ''),
        signed: get(userSignDoc, 'signed', false),
        viewed: get(userSignDoc, 'viewed', false),
        completedByUserId: get(userSignDoc, 'completedByUserId', ''),
        outcomeStatus: get(userSignDoc, 'outcomeStatus', null),
        // ACTIVE / ARCHIVED. Archived rows render read-only.
        status: get(userSignDoc, 'status', UserSignDocStatus.ACTIVE),
        uploadedFile: uploadedFile,
        expiryDate: get(userSignDoc, 'expiryDate', null),
        additionalData: get(userSignDoc, 'signDoc.additionalData', {}),
        isCurrentUserApprover: get(userSignDoc, 'signDoc.isCurrentUserApprover', false),
        // Tasks of a learning path, already in path order. Empty for every other type.
        learningPathChildren: map(get(userSignDoc, 'learningPathChildren', []), (child) => ({
            ...convertUserSignDocToRow(child),
            isLearningPathChild: true,
            learningPathOrder: get(child, 'learningPathOrder', null),
        })),
    }
}

const convertData = (signDocs) => {
    return map(signDocs, convertUserSignDocToRow)
}

const paginationModel = {
    page: 0,
    pageSize: 100,
}


export const UserDetailsModal = ({
                                   modalOpen,
                                   closeModal,
                                   user
                               }) => {

    const {i18n} = useContext(I18nReactContext)
    const companyContext = useContext(CompanyReactContext)
    const [allSignDocsData, setAllSignDocsData] = useState([])
    const [previewFile, setPreviewFile] = useState(null)
    const [approveModalOpen, setApproveModalOpen] = useState(false)
    const [rejectModalOpen, setRejectModalOpen] = useState(false)
    const [selectedUserSignDoc, setSelectedUserSignDoc] = useState(null)
    const [gridRefreshTrigger, setGridRefreshTrigger] = useState(0)
    const [expandedLearningPathIds, setExpandedLearningPathIds] = useState([])
    const [showArchived, setShowArchived] = useState(false)

    /**
     * Expands or collapses a learning path row.
     * Defined with an empty dependency list so the columns, which are built once, never hold a stale copy.
     * @param {string} signDocId - The learning path SignDoc ID
     * @returns {void}
     */
    const toggleLearningPath = useCallback((signDocId) => {
        setExpandedLearningPathIds(prevIds => (
            prevIds.includes(signDocId)
                ? filter(prevIds, id => id !== signDocId)
                : [...prevIds, signDocId]
        ))
    }, [])

    /**
     * Inserts the tasks of every expanded learning path right below their path row.
     * Runs on each render instead of re-fetching, so expanding is instant.
     * @param {Array} rows - The rows returned by the data converter
     * @returns {Array} Rows with the tasks of expanded learning paths spliced in
     */
    const rowsTransformer = (rows) => {
        return map(rows, (row) => {
            if (!isExpandableLearningPathRow(row)) {
                return [row]
            }

            const children = get(row, 'learningPathChildren', [])
            const isExpanded = expandedLearningPathIds.includes(row.id)
            const parentRow = {...row, isExpanded}

            if (!isExpanded) {
                return [parentRow]
            }

            // The grid id is scoped to the path, because the same SignDoc can be a task in two paths.
            // signDocId keeps the real SignDoc id for the row actions.
            const childRows = map(children, (child, index) => ({
                ...child,
                id: `${row.id}__${child.id}`,
                signDocId: child.id,
                learningPathRootId: row.id,
                isLastLearningPathChild: index === children.length - 1,
            }))

            return [parentRow, ...childRows]
        }).flat()
    }

    /**
     * Gives learning path rows the CSS classes that draw the frame around a path and its tasks.
     * @param {object} params - MUI DataGrid row params
     * @returns {string} Space separated CSS class names
     */
    const getRowClassName = (params) => {
        if (get(params, 'row.isLearningPathChild', false)) {
            return `UserDetailsModal__row--lp-child ${get(params, 'row.isLastLearningPathChild', false) ? 'UserDetailsModal__row--lp-child-last' : ''}`
        }

        if (get(params, 'row.isExpanded', false)) {
            return 'UserDetailsModal__row--lp-parent-expanded'
        }

        if (get(params, 'row.status') === UserSignDocStatus.ARCHIVED) {
            return 'UserDetailsModal__row--archived'
        }

        return ''
    }

    const removeUserFromSignDoc = async (signDocId) => {
        await removeUserSignDocAPI({signDocId, userId: user.id})
    }

    const markCompleted = async (signDocId, options = {}) => {
        await markSignDocUserAsCompletedAPI({signDocId, userId: user.id, ...options})
    }

    const unmarkCompleted = async (signDocId) => {
        await unmarkSignDocUserAsCompletedAPI({signDocId, userId: user.id})
    }

    const handlePreview = (file) => {
        if (isPreviewableFile(file.mimeType) && file.signedUrl) {
            setPreviewFile(file);
        }
    };

    const handleClosePreview = () => {
        setPreviewFile(null);
    };

    /**
     * Unified handler for transforming row data and opening approval modals
     * @param {object} row - The row data from the grid
     * @param {boolean} isApprove - True for approval, false for rejection
     */
    const handleOpenApprovalOrRejectModal = ({row, isApprove}) => {
        const transformedData = {
            id: get(row, 'userSignDocId', ''),
            user: {
                firstName: get(user, 'firstName', ''),
                lastName: get(user, 'lastName', '')
            },
            signDoc: {
                docName: get(row, 'docName', ''),
                category: get(row, 'category', ''),
                expirationDuration: get(row, 'additionalData.expiryDuration', null)
            },
            expiryDate: get(row, 'expiryDate', null)
        };

        setSelectedUserSignDoc(transformedData);

        if (isApprove) {
            setApproveModalOpen(true);
        } else {
            setRejectModalOpen(true);
        }
    }

    /**
     * Unified approve handler - handles both course attendance and upload document approval
     * For courses: directly marks as attended
     * For upload documents: opens approval modal for expiry date selection
     * @param {object} row - The row data containing signDoc information
     */
    const handleApproveAction = async (row) => {
        const signDocType = get(row, 'type', '');
        const isCourse = signDocType === SignDocTypes.ONLINE_COURSE || signDocType === SignDocTypes.FRONTAL_COURSE;

        if (isCourse) {
            // For courses: directly mark as attended
            try {
                await approveOrRejectUserUploadAPI({
                    userSignDocId: row.userSignDocId,
                    expiryDate: null,
                    isApproved: true
                });
                setGridRefreshTrigger(prev => prev + 1);
            } catch (error) {
                console.error('Error marking user as attended:', error);
            }
        } else {
            // For upload documents: open approval modal
            handleOpenApprovalOrRejectModal({row, isApprove: true});
        }
    };

    /**
     * Unified reject handler - handles both course no-show and upload document rejection
     * For courses: directly marks as no-show
     * For upload documents: opens rejection modal for reason entry
     * @param {object} row - The row data containing signDoc information
     */
    const handleRejectAction = async (row) => {
        const signDocType = get(row, 'type', '');
        const isCourse = signDocType === SignDocTypes.ONLINE_COURSE || signDocType === SignDocTypes.FRONTAL_COURSE;

        if (isCourse) {
            // For courses: directly mark as no-show
            try {
                await approveOrRejectUserUploadAPI({
                    userSignDocId: row.userSignDocId,
                    expiryDate: null,
                    isApproved: false
                });
                setGridRefreshTrigger(prev => prev + 1);
            } catch (error) {
                console.error('Error marking user as no-show:', error);
            }
        } else {
            // For upload documents: open rejection modal
            handleOpenApprovalOrRejectModal({row, isApprove: false});
        }
    };

    /**
     * Unified handler for processing approval/rejection of user uploads
     * @param {string} userSignDocId - The UserSignDoc ID to process
     * @param {boolean} isApproved - True for approval, false for rejection
     * @param {Date} [expiryDate] - Expiry date for approved documents
     * @param {string} [rejectionReason] - Reason for rejection
     */
    const handleApprovalOrRejectProcess = async ({userSignDocId, isApproved, expiryDate = null, rejectionReason = null }) => {
        try {
            await approveOrRejectUserUploadAPI({
                userSignDocId,
                isApproved,
                expiryDate,
                rejectionReason
            });

            // Close the appropriate modal
            if (isApproved) {
                setApproveModalOpen(false);
            } else {
                setRejectModalOpen(false);
            }

            setSelectedUserSignDoc(null);
            setGridRefreshTrigger(prev => prev + 1); // Trigger grid refresh
        } catch (error) {
            const action = isApproved ? 'updating expiry date' : 'declining approval';
            console.error(`Error ${action}:`, error);
            throw error;
        }
    };

    const handleApprove = async (userSignDocId, expiryDate) =>
        handleApprovalOrRejectProcess({userSignDocId, isApproved: true, expiryDate });

    const handleReject = async (userSignDocId, rejectionReason) =>
        handleApprovalOrRejectProcess({userSignDocId, isApproved: false, rejectionReason });

    const getColumns = async () => {
        const hasCertificationFF = companyContext?.featureFlags?.[FeatureFlags.CERTIFICATION] || false;

        return getUserDetailsColumns({
            i18n,
            removeUserFromSignDoc,
            markCompleted,
            unmarkCompleted,
            handlePreview,
            handleApproveAction,
            handleRejectAction,
            hasCertificationFF,
            toggleLearningPath
        })
    }

    /**
     * Switches between active assignments and the archive, and forces the grid to refetch.
     * @param {object} event - The switch change event
     */
    const handleShowArchivedChange = (event) => {
        setShowArchived(event.target.checked);
    };

    const apiToGetData = async () => {
        const signDocsArray = await getUserSignDocsByAdminAPI({
            types: getAllSignDocTypes(),
            showNonForceCompletion: false,
            showForceCompletion: true,
            forUserId: user.id,
            isDeletedUser: Boolean(get(user, 'deletedAt')),
            // The two sets are never mixed - the toggle asks for one or the other.
            userSignDocStatus: showArchived ? UserSignDocStatus.ARCHIVED : UserSignDocStatus.ACTIVE,
        });
        
        setAllSignDocsData(signDocsArray);
        
        return {data: signDocsArray, count: signDocsArray.length};
    }

    return (
        <>
            <Modal open={modalOpen} onClose={closeModal}>
                <div className="UserDetailsModal modalWrapper">
                    <div className="modalTitle">
                        {`${i18n.translate(get(user, 'deletedAt') ? 'createEditUserModal.userDetails' : 'createEditUserModal.userDetails')} - ${user.firstName} ${user.lastName}`}
                    </div>
                    {/* Progress summary - describes whichever set the toggle is showing */}
                    <UserProgressSummary signDocsData={allSignDocsData} i18n={i18n} />

                    <div className="UserDetailsModal__filters">
                        <label className="UserDetailsModal__archive-toggle">
                            <Switch
                                checked={showArchived}
                                onChange={handleShowArchivedChange}
                                inputProps={{'data-testid': 'user-signdoc-archive-toggle'}}
                            />
                            <span className="UserDetailsModal__archive-toggle-label">
                                {i18n.translate('userDetailsModal.statusFilter.archived')}
                            </span>
                        </label>
                    </div>

                    <div className="modalContent">
                        <GridReactContextProvider>
                            <DataGridWrapper
                                filterModel={tableFilters}
                                disableColumnFilter
                                paginationModel={paginationModel}
                                getColumns={getColumns}
                                apiToGetData={apiToGetData}
                                dataConverter={convertData}
                                rowsTransformer={rowsTransformer}
                                getRowClassName={getRowClassName}
                                key={`${gridRefreshTrigger}-${showArchived}`}
                            />
                        </GridReactContextProvider>
                    </div>
                </div>
            </Modal>

            {/* Approve/Update Expiry Date Modal */}
            <ApproveUploadModal
                open={approveModalOpen}
                onClose={() => setApproveModalOpen(false)}
                userSignDoc={selectedUserSignDoc}
                onApprove={handleApprove}
            />

            {/* Reject Upload Modal */}
            <RejectUploadModal
                open={rejectModalOpen}
                onClose={() => setRejectModalOpen(false)}
                userSignDoc={selectedUserSignDoc}
                onReject={handleReject}
            />

            {/* File Preview Modal */}
            <FilePreview
                file={previewFile}
                open={!!previewFile}
                onClose={handleClosePreview}
            />
        </>
    );
};
